/* The Stablecoins screen's own furniture: chart, league tables, peg badges
   and chain bars. Tiles and panel chrome come from terminal.css; colors are
   its page tokens plus one warm tone for a drifting peg. Tables scroll in
   their own frame so the page never scrolls sideways at phone width. */

.screen {
  --warn: #d9a441;
  min-width: 0;
}

.panel {
  margin: 0 0 12px;
}

.sourceLink {
  color: var(--signal);
  text-decoration: none;
}

.sourceLink:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.notice {
  margin: 0 0 12px;
  padding: 12px 14px;
  border: 1px solid var(--signal);
  background: var(--paper-raised);
  color: var(--ink-muted);
  font-size: 0.78rem;
}

.notice p {
  margin: 0;
}

.noticeTitle {
  margin: 0 0 4px !important;
  color: var(--signal);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.footnote {
  margin: 0;
  color: var(--ink-muted);
  font-size: 0.7rem;
  line-height: 1.6;
}

.absent {
  color: var(--ink-muted);
}

/* ---- tables ---- */

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--line);
}

.search {
  flex: 1 1 14rem;
  min-width: 0;
  max-width: 24rem;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid var(--line-strong);
  background: #000;
  color: var(--ink);
  font: inherit;
  font-size: 0.8rem;
}

.currency {
  flex: 0 1 auto;
  min-width: 0;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid var(--line-strong);
  background: #000;
  color: var(--ink);
  font: inherit;
  font-size: 0.8rem;
}

.scroll {
  max-width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
}

.table th {
  padding: 0 10px;
  border-bottom: 1px solid var(--line-strong);
  background: var(--head-fill);
  color: var(--signal);
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.table td {
  padding: 5px 10px 6px;
  border-bottom: 1px solid #161613;
  vertical-align: middle;
  white-space: nowrap;
}

.table tbody tr:nth-child(even) td {
  background: #050505;
}

.table tbody tr:hover td {
  background: #170d2a;
}

.table tbody tr:last-child td {
  border-bottom: none;
}

.table th[data-num="true"],
.table td[data-num="true"],
.table th[data-num=""],
.table td[data-num=""] {
  text-align: right;
}

.sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 30px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
}

.table th[data-num="true"] .sort {
  flex-direction: row-reverse;
}

.sort:hover,
.sort[data-sorted="true"] {
  color: var(--ink);
}

.sortMark {
  opacity: 0.6;
  font-size: 0.9em;
}

.sort[data-sorted="true"] .sortMark {
  opacity: 1;
}

.rank {
  width: 2.5em;
  color: var(--ink-muted);
  text-align: right;
}

.strong {
  color: var(--signal);
  font-weight: 700;
}

.coin {
  padding: 0;
  border: 0;
  background: none;
  color: var(--signal);
  cursor: pointer;
  font: inherit;
  font-weight: 700;
  text-align: left;
}

.coin:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.coin[aria-expanded="true"] {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.peg {
  margin-left: 6px;
  padding: 0 4px;
  border: 1px solid var(--line-strong);
  color: var(--ink-muted);
  font-size: 0.64rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  vertical-align: 1px;
}

/* DQ-23: a value Panther read on chain itself; the hover gives DefiLlama's figure. */
.onchain {
  margin: 0 6px;
  padding: 0 4px;
  border: 1px solid var(--line-strong);
  border-radius: 3px;
  color: var(--ink-muted);
  font-size: 0.6rem;
  letter-spacing: 0.04em;
  vertical-align: 1px;
  white-space: nowrap;
}

.coinName {
  display: block;
  max-width: 14em;
  overflow: hidden;
  color: var(--ink-muted);
  font-size: 0.68rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table tr[data-depeg="depegged"] td[data-col="price"] {
  color: var(--down);
}

.table tr[data-depeg="minor"] td[data-col="price"] {
  color: var(--warn);
}

/* ---- peg badge ---- */

.badge {
  display: inline-block;
  padding: 0 5px;
  border: 1px solid var(--line-strong);
  color: var(--ink-muted);
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.6;
  text-transform: uppercase;
}

.badge[data-tone="warn"] {
  border-color: var(--warn);
  color: var(--warn);
}

.badge[data-tone="down"] {
  border-color: var(--down);
  background: var(--down);
  color: #000;
}

.badge[data-tone="unknown"] {
  border-style: dashed;
}

/* ---- share and chain bars ---- */

.share {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 8.5em;
  white-space: nowrap;
}

.shareTrack {
  flex: 0 0 4.5em;
  height: 3px;
  background: #161613;
}

.shareFill {
  display: block;
  height: 100%;
  background: var(--signal);
}

.chainBar {
  display: inline-grid;
  gap: 3px;
  min-width: 12em;
}

.stack {
  display: flex;
  width: 100%;
  height: 5px;
  background: #161613;
}

.segment {
  display: block;
  height: 100%;
  min-width: 1px;
}

.segment + .segment {
  border-left: 1px solid var(--paper);
}

.segment[data-i="0"] {
  background: var(--signal);
}

.segment[data-i="1"] {
  background: #8b5cf6;
}

.segment[data-i="2"] {
  background: #7c3aed;
}

.segment[data-i="3"] {
  background: var(--signal-fill);
}

.segment[data-i="4"],
.segment[data-i="other"] {
  background: var(--line-strong);
}

.chainLead {
  color: var(--ink);
  font-size: 0.7rem;
}

.chainMore {
  color: var(--ink-muted);
}

/* ---- expanded chain split ---- */

.detailTr td {
  padding: 6px 10px 10px 28px;
  background: #0a0612 !important;
  white-space: normal;
}

.detail {
  display: grid;
  gap: 5px;
  max-width: 34rem;
}

.detailRow {
  display: grid;
  grid-template-columns: minmax(6rem, 10rem) minmax(4rem, 1fr) auto;
  align-items: center;
  gap: 10px;
  font-size: 0.74rem;
}

.detailTrack {
  height: 5px;
  background: #161613;
}

.detailFill {
  display: block;
  height: 100%;
  background: var(--signal);
}

.detailValue {
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}

.detailNote {
  margin: 0;
  color: var(--ink-muted);
  font-size: 0.7rem;
}

.more {
  display: block;
  width: 100%;
  padding: 7px;
  border: 0;
  border-top: 1px solid var(--line);
  background: #0d0d0d;
  color: var(--signal);
  cursor: pointer;
  font: inherit;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.more:hover {
  background: var(--signal-soft);
}

@media (max-width: 480px) {
  .table th,
  .table td {
    padding-inline: 7px;
  }

  .detailTr td {
    padding-left: 14px;
  }

  .share {
    min-width: 6.5em;
  }

  .shareTrack {
    flex-basis: 3em;
  }

  .chainBar {
    min-width: 9em;
  }

  .detailRow {
    grid-template-columns: minmax(4.5rem, 7rem) minmax(3rem, 1fr) auto;
  }

  /* Readable without scrolling the table sideways. */
  .detail .detailNote {
    max-width: calc(100vw - 64px);
  }
}

/* Under a history chart: where history starts, what the dashes mean. */
.historyNote {
  margin: 0;
  padding: 0 10px 10px;
  color: var(--ink-muted);
  font-family: var(--mono);
  font-size: 0.66rem;
}

/* CSV export of the filtered table (C-7). */
.csv {
  order: 3;
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid var(--line-strong);
  background: transparent;
  color: var(--signal);
  cursor: pointer;
  font: inherit;
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.csv:disabled {
  color: var(--ink-muted);
  cursor: default;
}


/* DQ-16: a value the jump guard holds at its last confirmed figure; the hover says why. */
.held {
  margin-left: 6px;
  padding: 0 4px;
  border: 1px solid var(--caution-line);
  border-radius: 3px;
  background: var(--caution-fill);
  color: var(--caution);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  vertical-align: 1px;
  cursor: help;
}
